import React, { Component } from "react";
import { DomPainter } from "@evergis/sgis/es/painters/DomPainter/DomPainter";
import { connect } from "react-redux";
import { getPrintEnabled } from "../../ducks/print/selectors";
import { RootState } from "../../ducks/reducers";

import {
  AreaContainer,
  LeftBottomCorner,
  LeftTopCorner,
  PrintBorder,
  RightBottomCorner,
  RightTopCorner,
} from "./styled";

import { setPrintRectangle } from "../../ducks/print/print";
import { withMapPainter } from "./hoc/withMapPainter";
import { Position, PrintRectangle } from "../../interfaces/print";

enum RectCorner {
  LeftTop,
  RightTop,
  LeftBottom,
  RightBottom,
}

type PrintAreaState = {
  isDragging: boolean;
  top: number;
  bottom: number;
  right: number;
  left: number;
  deltaX: number;
  deltaY: number;
  initialPosX: number | null;
  initialPosY: number | null;
  corner: RectCorner | null;
};

type PrintAreaProps = {
  setRectanglePosition?: (rectangle: PrintRectangle) => void;
  painter?: DomPainter;
  isPrintEnabled?: boolean;
};

export class PrintAreaBase extends Component<PrintAreaProps, PrintAreaState> {
  state = {
    isDragging: false,
    top: window.devicePixelRatio === 1 ? 200 : 100,
    bottom: window.devicePixelRatio === 1 ? 200 : 100,
    right: window.devicePixelRatio === 1 ? 300 : 50,
    left: window.devicePixelRatio === 1 ? 300 : 50,
    deltaX: 0,
    deltaY: 0,
    initialPosX: null,
    initialPosY: null,
    corner: null,
  };

  componentDidMount() {
    this.addListeners();
    this.setRectangle();
  }

  componentWillUnmount() {
    this.removeListeners();
  }

  addListeners() {
    addEventListener("resize", this.setRectangle);
    addEventListener("mousemove", this.resizeContainer);
    addEventListener("mouseup", this.stopResizeContainer);
    addEventListener("touchmove", this.resizeContainer);
    addEventListener("touchend", this.stopResizeContainer);
  }

  removeListeners() {
    removeEventListener("mousemove", this.resizeContainer);
    removeEventListener("mouseup", this.stopResizeContainer);
    removeEventListener("touchmove", this.resizeContainer);
    removeEventListener("touchend", this.stopResizeContainer);
    removeEventListener("resize", this.setRectangle);
  }

  setRectangleInner = () => {
    const { top, bottom, right, left } = this.state;
    const { width, height } = this.props.painter;
    const rectWidth = width - left - right;
    const rectHeight = height - top - bottom;

    this.props.setRectanglePosition({
      top,
      bottom,
      right,
      left,
      width: rectWidth,
      height: rectHeight,
    });
  };

  setRectangle = () => {
    // important to do it on RAF because DomPainter updates sizes on RAF
    requestAnimationFrame(this.setRectangleInner);
  };

  isValidSize = (position: PrintRectangle): boolean => {
    const { right, left, top, bottom } = this.state;

    if (position.left < 0 || position.top < 0) return false;

    const borderSides = Object.keys(position);
    const { width, height } = this.props.painter;
    const maxSideLength = 80;

    const imageWidth = borderSides[0] === Position.Left ? width - right - position.left : width - left - position.right;
    const imageHeight =
      borderSides[1] === Position.Top ? height - bottom - position.top : height - top - position.bottom;

    return imageWidth >= maxSideLength && imageHeight >= maxSideLength;
  };

  resizeContainer = (evt: MouseEvent | TouchEvent) => {
    if (!this.state.isDragging) return;

    window.getSelection && window.getSelection().removeAllRanges();

    const { corner, initialPosX, initialPosY } = this.state;
    const clientX = "touches" in evt && evt.touches ? evt.touches[0].clientX : (evt as MouseEvent).clientX;
    const clientY = "touches" in evt && evt.touches ? evt.touches[0].clientY : (evt as MouseEvent).clientY;

    const currentDeltaX = clientX - initialPosX;
    const currentDeltaY = clientY - initialPosY;

    switch (corner) {
      case RectCorner.LeftTop:
        this.setState(state => {
          const nextLeft = state.left + currentDeltaX - state.deltaX;

          if (this.isValidSize({ left: nextLeft, top: clientY })) {
            return {
              deltaX: currentDeltaX,
              top: clientY,
              left: nextLeft,
            };
          }
        });

        break;
      case RectCorner.LeftBottom:
        this.setState(state => {
          const nextLeft = state.left + currentDeltaX - state.deltaX;
          const nextBottom = state.bottom - currentDeltaY + state.deltaY;

          if (this.isValidSize({ left: nextLeft, bottom: nextBottom })) {
            return {
              deltaX: currentDeltaX,
              deltaY: currentDeltaY,
              bottom: nextBottom,
              left: nextLeft,
            };
          }
        });
        break;
      case RectCorner.RightTop:
        this.setState(state => {
          const nextRight = state.right - currentDeltaX + state.deltaX;

          if (this.isValidSize({ right: nextRight, top: clientY })) {
            return {
              deltaX: currentDeltaX,
              top: clientY,
              right: nextRight,
            };
          }
        });
        break;
      case RectCorner.RightBottom:
        this.setState(state => {
          const nextRight = state.right - currentDeltaX + state.deltaX;
          const nextBottom = state.bottom - currentDeltaY + state.deltaY;

          if (this.isValidSize({ right: nextRight, bottom: nextBottom })) {
            return {
              deltaX: currentDeltaX,
              deltaY: currentDeltaY,
              right: nextRight,
              bottom: nextBottom,
            };
          }
        });
        break;
      default:
        break;
    }
  };

  startResizeContainer = (evt: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>, corner: RectCorner) => {
    this.setState({
      isDragging: true,
      initialPosX:
        "touches" in evt && evt.touches ? evt.touches[0].clientX : (evt as React.MouseEvent<HTMLElement>).clientX,
      initialPosY:
        "touches" in evt && evt.touches ? evt.touches[0].clientY : (evt as React.MouseEvent<HTMLElement>).clientY,

      corner,
    });
  };

  stopResizeContainer = () => {
    if (this.state.corner !== null) {
      this.setRectangle();
    }

    this.setState({
      corner: null,
      isDragging: false,
      initialPosY: null,
      initialPosX: null,
      deltaX: 0,
      deltaY: 0,
    });
  };

  render() {
    const { top, bottom, right, left } = this.state;
    const { isPrintEnabled } = this.props;

    if (!isPrintEnabled) {
      return null;
    }

    return (
      <AreaContainer top={top} bottom={bottom} right={right} left={left}>
        <PrintBorder>
          <LeftTopCorner
            onMouseDown={evt => this.startResizeContainer(evt, RectCorner.LeftTop)}
            onTouchStart={evt => this.startResizeContainer(evt, RectCorner.LeftTop)}
          />
          <RightTopCorner
            onMouseDown={evt => this.startResizeContainer(evt, RectCorner.RightTop)}
            onTouchStart={evt => this.startResizeContainer(evt, RectCorner.RightTop)}
          />
          <LeftBottomCorner
            onMouseDown={evt => this.startResizeContainer(evt, RectCorner.LeftBottom)}
            onTouchStart={evt => this.startResizeContainer(evt, RectCorner.LeftBottom)}
          />
          <RightBottomCorner
            onMouseDown={evt => this.startResizeContainer(evt, RectCorner.RightBottom)}
            onTouchStart={evt => this.startResizeContainer(evt, RectCorner.RightBottom)}
          />
        </PrintBorder>
      </AreaContainer>
    );
  }
}

export const PrintArea = connect((state: RootState) => ({ isPrintEnabled: getPrintEnabled(state) }), { setRectanglePosition: setPrintRectangle })(withMapPainter(PrintAreaBase));
